import { MIN_TEXT_WIDTH } from "../constantsV2";
import type {
    DiagonalResizeParams,
    DiagonalResizeResult,
    EditingDiagonalResizeParams,
    EditingVerticalResizeParams,
    HorizontalResizeResult,
    TextLine,
    TextV2Data,
    VerticalResizeParams,
    VerticalResizeResult,
} from "../typesV2";
import { calculateLineHeight, calculateLineWidths } from "./measurements";
import { reflowTextLines } from "./wrap";

export const resizeHorizontal = (
    x: number,
    hardWidth: number,
    rawOriginalLines: TextLine[],
    data: TextV2Data,
    horizontalPadding: number,
    lineHeight: number,
): HorizontalResizeResult | null => {
    if (x === 0) return null;

    const nextHardWidth = Math.max(1, hardWidth + x);
    const reflow = reflowTextLines(
        rawOriginalLines,
        nextHardWidth,
        data,
        horizontalPadding,
        lineHeight,
    );

    return { hardWidth: nextHardWidth, lines: reflow.lines, lineWidths: reflow.lineWidths };
};

export const resizeDiagonal = (
    x: number,
    { diagonalResizeState, data, lines }: DiagonalResizeParams,
): DiagonalResizeResult | null => {
    if (!diagonalResizeState) return null;

    let potentialAccumulatedX = diagonalResizeState.accumulatedX + x;
    let potentialWidth = diagonalResizeState.initialWidth + potentialAccumulatedX;
    const minWidth = Math.max(1, MIN_TEXT_WIDTH);
    if (potentialWidth < minWidth) {
        potentialWidth = minWidth;
        potentialAccumulatedX = minWidth - diagonalResizeState.initialWidth;
    }

    const potentialScale = potentialWidth / diagonalResizeState.initialWidth;
    const potentialFontSize = diagonalResizeState.initialFontSize * potentialScale;
    if (potentialFontSize < 1) {
        return null;
    }

    const nextState = {
        ...diagonalResizeState,
        accumulatedX: potentialAccumulatedX,
    };
    const baseLines = diagonalResizeState.initialLines ?? lines;

    const nextData: TextV2Data = { ...data, fontSize: potentialFontSize };
    const metrics = calculateLineHeight(nextData);
    const lineWidths = calculateLineWidths(
        baseLines,
        nextData,
        metrics.horizontalPadding,
        metrics.lineHeight,
    );

    return {
        hardWidth: potentialWidth,
        fontSize: potentialFontSize,
        lineHeight: metrics.lineHeight,
        verticalPadding: metrics.verticalPadding,
        horizontalPadding: metrics.horizontalPadding,
        lineWidths,
        lines: baseLines,
        diagonalResizeState: nextState,
    };
};

export const resizeVertical = (
    y: number,
    { resizeState, data, lines }: VerticalResizeParams,
): VerticalResizeResult | null => {
    if (!resizeState) return null;

    let potentialAccumulatedY = resizeState.accumulatedY + y;
    let potentialHeight = resizeState.initialHeight + potentialAccumulatedY;
    const minScale = 1 / resizeState.initialFontSize;
    const minHeight = resizeState.initialHeight * minScale;
    if (potentialHeight < minHeight) {
        potentialHeight = minHeight;
        potentialAccumulatedY = minHeight - resizeState.initialHeight;
    }

    const potentialScale = potentialHeight / resizeState.initialHeight;
    const potentialFontSize = resizeState.initialFontSize * potentialScale;

    const nextState = {
        ...resizeState,
        accumulatedY: potentialAccumulatedY,
    };
    const baseLines = resizeState.initialLines ?? lines;

    const nextData: TextV2Data = { ...data, fontSize: potentialFontSize };
    const metrics = calculateLineHeight(nextData);
    const lineWidths = calculateLineWidths(
        baseLines,
        nextData,
        metrics.horizontalPadding,
        metrics.lineHeight,
    );
    const potentialHardWidth = resizeState.initialWidth * potentialScale;
    const hardWidth = Math.max(potentialHardWidth, MIN_TEXT_WIDTH);

    return {
        fontSize: potentialFontSize,
        lineHeight: metrics.lineHeight,
        verticalPadding: metrics.verticalPadding,
        horizontalPadding: metrics.horizontalPadding,
        lineWidths,
        lines: baseLines,
        hardWidth,
        resizeState: nextState,
    };
};

export const editingResizeDiagonal = ({
    resizeState,
    deferredResize,
}: EditingDiagonalResizeParams) => {
    const { initialWidth, initialFontSize } = resizeState;
    const { x } = deferredResize;

    const nextWidth = Math.max(MIN_TEXT_WIDTH, initialWidth + x);
    const scale = nextWidth / initialWidth;
    const nextFontSize = initialFontSize * scale;

    return { nextFontSize, nextWidth };
};

export const editingResizeVertical = ({
    resizeState,
    deferredResize,
}: EditingVerticalResizeParams) => {
    const { initialFontSize, initialHeight, initialWidth } = resizeState;
    const { y } = deferredResize;

    const minScale = 1 / initialFontSize;
    const minHeight = initialHeight * minScale;

    const nextHeight = Math.max(minHeight, initialHeight + y);
    const scale = nextHeight / initialHeight;
    const nextWidth = Math.max(MIN_TEXT_WIDTH, initialWidth * scale);
    const nextFontSize = initialFontSize * scale;

    return { nextWidth, nextFontSize };
};
